export default {
  namespaced: true,
  state: {
    activeMobile: null,
    activeBorrowerName: '',
    panelOpen: false,
    comments: [],
    // Mobile the current `comments` array was actually fetched for. The
    // `comments` getter refuses to expose the array unless this matches the
    // active mobile, so one consumer's notes can never render under another.
    commentsMobile: null,
    // Monotonic counter bumped on every consumer selection / panel open. The
    // widget watches it and forces a fresh fetch, so reloading never depends on
    // a watcher happening to detect a value change.
    fetchNonce: 0,
    loading: false,
    // True when a list row's comment icon picked the consumer. The list has no
    // single borrower of its own, so the launcher stays hidden there and
    // closing the panel leaves nothing behind. Any other selection (a details
    // page, the router, the Leads view-lead dialog) resets it.
    openedFromList: false,
  },
  getters: {
    isVisible: (state) => !!state.activeMobile,
    activeMobile: (state) => state.activeMobile,
    openedFromList: (state) => state.openedFromList,
    activeBorrowerName: (state) => state.activeBorrowerName,
    panelOpen: (state) => state.panelOpen,
    comments: (state) =>
      state.commentsMobile && state.commentsMobile === state.activeMobile
        ? state.comments
        : [],
    fetchNonce: (state) => state.fetchNonce,
    loading: (state) => state.loading,
  },
  mutations: {
    SET_ACTIVE_MOBILE(state, mobile) {
      const normalized = mobile ? String(mobile) : null;
      if (state.activeMobile !== normalized) {
        state.comments = [];
        state.commentsMobile = null;
        state.activeBorrowerName = '';
      }
      state.activeMobile = normalized;
      state.openedFromList = false;
      // Force a reload for the selected consumer — even when the number is
      // unchanged (e.g. reselecting the same row) — so the panel is never left
      // showing whatever was last loaded.
      if (normalized) state.fetchNonce += 1;
    },
    CLEAR_ACTIVE_MOBILE(state) {
      state.activeMobile = null;
      state.activeBorrowerName = '';
      state.panelOpen = false;
      state.comments = [];
      state.commentsMobile = null;
      state.openedFromList = false;
    },
    SET_OPENED_FROM_LIST(state, value) {
      state.openedFromList = !!value;
    },
    SET_PANEL_OPEN(state, value) {
      state.panelOpen = !!value;
      // Reopening the panel for the active consumer refreshes its comments.
      if (value && state.activeMobile) state.fetchNonce += 1;
    },
    TOGGLE_PANEL(state) {
      state.panelOpen = !state.panelOpen;
      // Opening the box via the comment FAB must refresh, like SET_PANEL_OPEN.
      if (state.panelOpen && state.activeMobile) state.fetchNonce += 1;
    },
    SET_COMMENTS(state, payload) {
      const { list, mobile } = payload || {};
      state.comments = Array.isArray(list) ? list : [];
      state.commentsMobile = mobile ? String(mobile) : null;
    },
    SET_BORROWER_NAME(state, name) {
      state.activeBorrowerName = name || '';
    },
    SET_LOADING(state, value) {
      state.loading = !!value;
    },
  },
  actions: {
    setActiveMobile({ commit }, mobile) {
      commit('SET_ACTIVE_MOBILE', mobile);
    },
    clearActiveMobile({ commit }) {
      commit('CLEAR_ACTIVE_MOBILE');
    },
    openPanel({ commit }) {
      commit('SET_PANEL_OPEN', true);
    },
    // A list row's comment icon: open this consumer's thread without making
    // them the page's borrower, so no launcher is left on the list afterwards.
    openFromList({ commit }, mobile) {
      if (!mobile) return;
      commit('SET_ACTIVE_MOBILE', mobile);
      commit('SET_OPENED_FROM_LIST', true);
      commit('SET_PANEL_OPEN', true);
    },
    // Closing the sidebar closes the sidebar — nothing more. This used to commit
    // CLEAR_ACTIVE_MOBILE, which dropped the active consumer, and since the
    // launcher renders on `v-if="activeMobile"` the button itself disappeared
    // the first time you closed the panel: the only way to get it back was to
    // reselect the row. Genuinely forgetting the consumer is `clearActiveMobile`,
    // which is what every route-leave hook already calls.
    closePanel({ commit }) {
      commit('SET_PANEL_OPEN', false);
    },
    togglePanel({ commit }) {
      commit('TOGGLE_PANEL');
    },
  },
};
